import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FileText,
  X,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  Upload,
  Calendar,
  User,
  Tag,
  Link as LinkIcon,
  Edit3
} from "lucide-react";

const SubmitNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    description: "",
    date: "",
    author: "",
    category: "",
    link: "",
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Animation states
  const [animatePage, setAnimatePage] = useState(false);

  // Fetch submitted news (OPD side)
  useEffect(() => {
    setAnimatePage(true);
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (user && user.email) {
        const res = await axios.get(`https://aaapd.org.et/gallery_backend/news/getNewsOPD.php?email=${encodeURIComponent(user.email)}`);
        setNewsList(Array.isArray(res.data) ? res.data : []);
      } else {
        setNewsList([]);
        showMessage("User not authenticated. Please log in again.", "error");
      }
    } catch (error) {
      console.error("Error fetching news:", error);
      setNewsList([]);
      showMessage("Failed to fetch your news submissions.", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setMessage("");

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key]) {
        data.append(key, formData[key]);
      }
    });

    // Add OPD ID for filtering - get from backend using email
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user && user.email) {
      data.append("email", user.email);
    }

    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/news/addNewsOPD.php",
        data,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      showMessage(res.data.message || "News submitted successfully");
      if (res.data.status === "success") {
        setNewsList([...newsList, { ...formData, status: "pending" }]);
        setFormData({
          title: "",
          excerpt: "",
          description: "",
          date: "",
          author: "",
          category: "",
          link: "",
          image: null,
        });
        setShowForm(false);
      }
    } catch (err) {
      console.error("Error submitting news:", err);
      showMessage("Error submitting news. Please try again.", "error");
      setFormLoading(false);
    }
  };

  return (
    <div
      className={`bg-gray-50 min-h-screen p-4 sm:p-6 transition-opacity duration-1000 ease-in-out ${
        animatePage ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My News Submissions</h1>
            <p className="text-gray-600 mt-1">Submit and track your news articles</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 shadow-md hover:shadow-lg mt-6 sm:mt-8"
          >
            <PlusCircle className="w-5 h-5" />
            Submit New Article
          </button>
        </header>

        {/* Message */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              messageType === "error"
                ? "bg-red-100 border-l-4 border-red-500 text-red-700"
                : "bg-green-100 border-l-4 border-green-500 text-green-700"
            }`}
          >
            <div className="flex items-center gap-2">
              {messageType === "error" ? (
                <AlertCircle className="w-5 h-5" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              {message}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="flex items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
              <p className="text-gray-600">Loading your news submissions...</p>
            </div>
          </div>
        ) : (
          <>
            {newsList.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
                <p className="mt-1 text-sm text-gray-500">You haven't submitted any news articles yet.</p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
                  >
                    <PlusCircle className="w-5 h-5" />
                    Submit Your First Article
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white shadow overflow-hidden rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Your Articles</h3>
                  <p className="text-sm text-gray-500">Track the status of your news submissions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {newsList.map((news) => (
                        <tr key={news.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <img
                              className="h-16 w-16 rounded-md object-cover"
                              src={news.image ? `https://aaapd.org.et/gallery_backend/news_opd/${news.image}` : "/placeholder.png"}
                              alt={news.title || 'News image'}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                              {news.title || <span className="text-gray-400">No title</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {news.author || <span className="text-gray-400">N/A</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {news.category || <span className="text-gray-400">N/A</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              news.status === "approved" ? "bg-green-100 text-green-800" :
                              news.status === "rejected" ? "bg-red-100 text-red-800" :
                              "bg-yellow-100 text-yellow-800"
                            }`}>
                              {news.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {news.date ? new Date(news.date).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Enhanced Modal Form */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-2 sm:mx-4 relative transform transition-all">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Edit3 className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-800">Submit News Article</h2>
                  </div>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowForm(false)}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {message && (
                  <div
                    className={`p-4 rounded-lg ${
                      messageType === "error"
                        ? "bg-red-100 border-l-4 border-red-500 text-red-700"
                        : "bg-green-100 border-l-4 border-green-500 text-green-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {messageType === "error" ? (
                        <AlertCircle className="w-5 h-5" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                      {message}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="inline w-4 h-4 mr-1" />
                      Author *
                    </label>
                    <input
                      type="text"
                      name="author"
                      placeholder="Enter author name"
                      value={formData.author}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Tag className="inline w-4 h-4 mr-1" />
                      Category
                    </label>
                    <input
                      type="text"
                      name="category"
                      placeholder="News category"
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Edit3 className="inline w-4 h-4 mr-1" />
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Enter news title"
                      value={formData.title}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Short Excerpt
                    </label>
                    <textarea
                      name="excerpt"
                      placeholder="Brief summary of the news article"
                      value={formData.excerpt}
                      onChange={handleChange}
                      rows={1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline w-4 h-4 mr-1" />
                      Date *
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <LinkIcon className="inline w-4 h-4 mr-1" />
                      Link (Optional)
                    </label>
                    <input
                      type="url"
                      name="link"
                      placeholder="https://..."
                      value={formData.link}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Description
                  </label>
                  <textarea
                    name="description"
                    placeholder="Detailed description of the news article"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Featured Image
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm w-full sm:w-auto order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading || !formData.title || !formData.author || !formData.date}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 text-sm justify-center w-full sm:w-auto order-1 sm:order-2"
                  >
                    {formLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Submit Article
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitNews;
